// ---------------------------------------------------------------------------
// Plugin system — equip / unequip + effect aggregation.
//
// Pairs with `@/data/plugins` (the seed catalog). The catalog there is
// intentionally minimal (just evolution unlocks for now). This module owns
// the runtime concerns:
//   * Loadouts (which plugins a member has installed)
//   * Slot budget (capacity per species, cost per plugin)
//   * Effect aggregation (stat multipliers, granted moves, triggered hooks,
//     unlocked evolutions) consumed by BattleEngine and the stats screen.
//
// When the catalog later grows fields like `kind`, `slotCost`,
// `effect.statBoosts`, `grantsMoveId`, etc., this module already reads them
// defensively via a structural read-shape — no changes needed here beyond
// removing the local defaults.
// ---------------------------------------------------------------------------

import type { Plugin, PluginEvolutionUnlock } from '@/data/plugins';
import { PLUGIN_CATALOG, findPluginById } from '@/data/plugins';

// --- Public types ----------------------------------------------------------

export type PluginLoadout = {
  speciesId: string;
  installedPluginIds: string[]; // ordered list of equipped plugin ids
  usedSlots: number;            // cached sum of slotCost for O(1) lookup
  // Back-compat alias for older callers (plugin-evolution.ts) that read
  // `equipped`. Kept in sync with `installedPluginIds`.
  equipped: string[];
};

export type StatMultipliers = {
  atk: number;
  def: number;
  spd: number;
  hp: number;
  accuracy: number;
  evasion: number;
  critRate: number;
};

// Back-compat shape for `plugin-evolution.ts` — extends the simple
// {fromSpecies,toSpecies} pair with the original `minLevel` gate and the
// `pluginId` that produced the unlock.
export type UnlockedEvolution = PluginEvolutionUnlock & {
  pluginId: string;
};

export type PluginEffectsSummary = {
  grantedMoves: string[];
  statMultipliers: StatMultipliers;
  onTurnStartEffects: string[];
  onTakeDamageEffects: string[];
  unlockedEvolutions: UnlockedEvolution[];
};

export type InstallSuccess = { ok: true; loadout: PluginLoadout };
export type InstallFailure = { ok: false; reason: InstallFailureReason };
export type InstallResult = InstallSuccess | InstallFailure;

export type InstallFailureReason =
  | 'not_enough_slots'
  | 'already_installed'
  | 'plugin_unknown'
  | 'wrong_species';

// --- Internal: read-shape over Plugin --------------------------------------
//
// We read fields the catalog *may* gain later. Today only `id`, `name`,
// `category`, and `unlocksEvolution` exist; the rest fall back to defaults.
// Keeping this structural read-shape avoids `any` and means future catalog
// growth doesn't ripple through this module.

type StatBoostKey = keyof StatMultipliers;

type PluginReadShape = {
  id: string;
  name?: string;
  category?: string;
  kind?: 'skill' | 'tool' | 'capability';
  slotCost?: number;
  grantsMoveId?: string;
  effect?: {
    statBoosts?: Partial<Record<StatBoostKey, number>>;
    onTurnStart?: string;
    onTakeDamage?: string;
  };
  unlocksEvolution?: {
    fromSpecies?: string;
    toSpecies: string;
    minLevel?: number;
  };
};

function asReadShape(plugin: Plugin): PluginReadShape {
  return plugin as unknown as PluginReadShape;
}

// Default slot cost when the catalog entry omits one. Keep at 1 so the
// loadout math behaves sensibly with the current minimal catalog.
const DEFAULT_SLOT_COST = 1;

function slotCostOf(plugin: PluginReadShape): number {
  return typeof plugin.slotCost === 'number' && plugin.slotCost >= 0
    ? plugin.slotCost
    : DEFAULT_SLOT_COST;
}

// Infer the runtime kind from explicit `kind` field if present, otherwise
// from `category` / `unlocksEvolution`:
//   * unlocksEvolution → 'capability'
//   * category 'combat' → 'tool' (likely grants a move once catalog grows)
//   * otherwise → 'skill'
function kindOf(plugin: PluginReadShape): 'skill' | 'tool' | 'capability' {
  if (plugin.kind) return plugin.kind;
  if (plugin.unlocksEvolution) return 'capability';
  if (plugin.category === 'combat') return 'tool';
  return 'skill';
}

// Default per-species slot capacity. The catalog does not (yet) expose a
// `SPECIES_PLUGIN_SLOTS` table, so we use a single flat capacity. When the
// data layer adds it, swap this for a lookup — the public function name
// stays the same.
const DEFAULT_SPECIES_CAPACITY = 3;

function speciesCapacity(speciesId: string): number {
  // Reserved hook for per-species overrides. Currently every species gets
  // the same budget. `speciesId` is intentionally read to keep the signature
  // future-proof and to silence unused-arg lint.
  void speciesId;
  return DEFAULT_SPECIES_CAPACITY;
}

function emptyMultipliers(): StatMultipliers {
  return {
    atk: 1,
    def: 1,
    spd: 1,
    hp: 1,
    accuracy: 1,
    evasion: 1,
    critRate: 1,
  };
}

// --- Public API ------------------------------------------------------------

export function newLoadout(speciesId: string): PluginLoadout {
  return {
    speciesId,
    installedPluginIds: [],
    usedSlots: 0,
    equipped: [],
  };
}

export function freeSlots(loadout: PluginLoadout): number {
  return Math.max(0, speciesCapacity(loadout.speciesId) - loadout.usedSlots);
}

export function installPlugin(
  loadout: PluginLoadout,
  pluginId: string
): InstallResult {
  const plugin = findPluginById(pluginId);
  if (!plugin) return { ok: false, reason: 'plugin_unknown' };

  if (loadout.installedPluginIds.includes(pluginId)) {
    return { ok: false, reason: 'already_installed' };
  }

  const shape = asReadShape(plugin);

  // Capability species-lock: if the plugin's unlocksEvolution specifies a
  // `fromSpecies` and it doesn't match the loadout's species, refuse.
  // Generic capabilities (no fromSpecies) are always allowed.
  if (kindOf(shape) === 'capability' && shape.unlocksEvolution) {
    const from = shape.unlocksEvolution.fromSpecies;
    if (from !== undefined && from !== loadout.speciesId) {
      return { ok: false, reason: 'wrong_species' };
    }
  }

  const capacity = speciesCapacity(loadout.speciesId);
  const nextUsed = loadout.usedSlots + slotCostOf(shape);
  if (nextUsed > capacity) {
    return { ok: false, reason: 'not_enough_slots' };
  }

  const nextIds = [...loadout.installedPluginIds, pluginId];
  return {
    ok: true,
    loadout: {
      speciesId: loadout.speciesId,
      installedPluginIds: nextIds,
      usedSlots: nextUsed,
      equipped: nextIds,
    },
  };
}

export function uninstallPlugin(
  loadout: PluginLoadout,
  pluginId: string
): PluginLoadout {
  if (!loadout.installedPluginIds.includes(pluginId)) {
    return loadout;
  }

  const remainingIds = loadout.installedPluginIds.filter(id => id !== pluginId);

  // Recompute from remaining ids — robust against catalog edits between
  // install and uninstall, and against an unknown id slipping into the list.
  const remainingCost = remainingIds.reduce<number>((sum, id) => {
    const p = findPluginById(id);
    return sum + (p ? slotCostOf(asReadShape(p)) : 0);
  }, 0);

  return {
    speciesId: loadout.speciesId,
    installedPluginIds: remainingIds,
    usedSlots: remainingCost,
    equipped: remainingIds,
  };
}

export function computeEffects(loadout: PluginLoadout): PluginEffectsSummary {
  const multipliers = emptyMultipliers();
  const grantedMoves: string[] = [];
  const onTurnStartEffects: string[] = [];
  const onTakeDamageEffects: string[] = [];
  const unlockedEvolutions: UnlockedEvolution[] = [];

  const statKeys: StatBoostKey[] = [
    'atk',
    'def',
    'spd',
    'hp',
    'accuracy',
    'evasion',
    'critRate',
  ];

  for (const pluginId of loadout.installedPluginIds) {
    const plugin = findPluginById(pluginId);
    if (!plugin) continue;
    const shape = asReadShape(plugin);
    const kind = kindOf(shape);

    // Skill plugins → additive stat multiplier deltas.
    // e.g. two +0.1 atk boosts → atk multiplier of 1.2.
    if (kind === 'skill' && shape.effect?.statBoosts) {
      const boosts = shape.effect.statBoosts;
      for (const key of statKeys) {
        const delta = boosts[key];
        if (typeof delta === 'number') {
          multipliers[key] += delta;
        }
      }
    }

    // Tool plugins → granted moves (dedup).
    if (kind === 'tool' && shape.grantsMoveId) {
      if (!grantedMoves.includes(shape.grantsMoveId)) {
        grantedMoves.push(shape.grantsMoveId);
      }
    }

    // Capability plugins → unlocked evolutions. Carries `minLevel` and
    // `pluginId` so the evolution resolver can gate + attribute the unlock.
    if (kind === 'capability' && shape.unlocksEvolution) {
      const from = shape.unlocksEvolution.fromSpecies ?? loadout.speciesId;
      unlockedEvolutions.push({
        fromSpecies: from,
        toSpecies: shape.unlocksEvolution.toSpecies,
        minLevel: shape.unlocksEvolution.minLevel,
        pluginId: shape.id,
      });
    }

    // Triggered effects — collected as opaque string ids; BattleEngine
    // resolves them at the appropriate phase.
    if (shape.effect?.onTurnStart) {
      onTurnStartEffects.push(shape.effect.onTurnStart);
    }
    if (shape.effect?.onTakeDamage) {
      onTakeDamageEffects.push(shape.effect.onTakeDamage);
    }
  }

  return {
    grantedMoves,
    statMultipliers: multipliers,
    onTurnStartEffects,
    onTakeDamageEffects,
    unlockedEvolutions,
  };
}

export function applyStatsMultipliers(
  baseStats: { atk: number; def: number; spd: number },
  multipliers: { atk: number; def: number; spd: number }
): { atk: number; def: number; spd: number } {
  return {
    atk: Math.round(baseStats.atk * multipliers.atk),
    def: Math.round(baseStats.def * multipliers.def),
    spd: Math.round(baseStats.spd * multipliers.spd),
  };
}

// Re-export catalog so consumers can iterate without importing from two
// places. Intentional convenience — not part of the core contract.
export { PLUGIN_CATALOG };
